/**
 * LF360-1970 — the order the pieces of a portal logout happen in, and what
 * happens when one of them fails.
 *
 * Two things end a session and they are not equally important:
 *
 * 1. **The cookie**, which only the server can clear. This is the half that was
 *    wrong twice. First it was never revoked at all — the page cleared
 *    `localStorage` before the request went out, so the request was
 *    unauthenticated and the token stayed live for thirty days. Then, with the
 *    cookie, a failure here was logged and the logout carried on regardless:
 *    the browser still had the cookie, so a reload signed the previous
 *    administrator straight back in. On a shared computer that is the whole of
 *    the problem this ticket exists for. So a failure stops the logout and the
 *    caller keeps the session on screen.
 *
 * 2. **A bearer token from before the cookie release**, which is best-effort. It
 *    is cleared from this browser either way, and the request that declines it
 *    server-side can fail without leaving anything behind that a reload would
 *    restore.
 *
 * Extracted from `profile-bar` because the order IS the fix and could not be
 * tested there: the component needs the store, the router, a dropdown and three
 * modals before it renders, so a spec for "does a failed revocation stop the
 * logout" would have been a spec for the mocks. Here it is two calls and a
 * boolean. `shared/utils/logoutSequence.js` in the web app is the same shape for
 * the same reason.
 *
 * @param {object} steps
 * @param {() => Promise<unknown>} steps.endSession revokes the cookie session
 * @param {string|null} steps.legacyToken a bearer from before the cookie, if any
 * @param {() => Promise<unknown>} steps.legacyLogout declines that bearer
 * @returns {Promise<boolean>} whether the session was really ended
 */
export const logOutOfPortal = async ({ endSession, legacyToken, legacyLogout }) => {
  try {
    await endSession();
  } catch (error) {
    console.warn("Could not end the session:", error?.message);

    return false;
  }

  if (legacyToken) {
    await legacyLogout().catch((error) =>
      console.warn("Logout request failed:", error?.message)
    );
  }

  return true;
};

export default logOutOfPortal;
